import {
  formatMicroUsdToUsd,
  makeColumnsForCredits,
} from "@app/components/poke/credits/columns";
import { PokeDataTableConditionalFetch } from "@app/components/poke/PokeConditionalDataTables";
import type { PokeStripeSubscriptionWire } from "@app/lib/api/poke/workspace_info";
import { LoadingBlock, safeLazy, Tooltip } from "@dust-tt/sparkle";
import { Suspense } from "react";

const PokeProgrammaticCostChart = safeLazy(() =>
  import("@app/components/poke/credits/PokeProgrammaticCostChart").then(
    (mod) => ({
      default: mod.PokeProgrammaticCostChart,
    })
  )
);

function PokeChartFallback() {
  return <LoadingBlock className="h-96 rounded-lg" />;
}

import { PokeDataTable } from "@app/components/poke/shadcn/ui/data_table";
import type { PokeCreditsData } from "@app/poke/swr/credits";
import { usePokeCredits } from "@app/poke/swr/credits";
import type { PokeCreditType } from "@app/types/api/poke/credits";
import type { SubscriptionType } from "@app/types/plan";
import type { WorkspaceType } from "@app/types/user";

const ONE_DOLLAR_MICRO_USD = 1_000_000;

interface CreditsDataTableProps {
  owner: WorkspaceType;
  subscription: SubscriptionType;
  stripeSubscription: PokeStripeSubscriptionWire | null;
  loadOnInit?: boolean;
}

function sortRowsByStartDateDescending(
  rows: PokeCreditType[]
): PokeCreditType[] {
  return [...rows].sort((a, b) => {
    const aStart = a.startDate ? new Date(a.startDate).getTime() : null;
    const bStart = b.startDate ? new Date(b.startDate).getTime() : null;
    // Null start dates go first (pending credits).
    if (aStart === null && bStart === null) {
      return 0;
    }
    if (aStart === null) {
      return -1;
    }
    if (bStart === null) {
      return 1;
    }
    return bStart - aStart;
  });
}

export function CreditsDataTable({
  owner,
  subscription,
  stripeSubscription,
  loadOnInit,
}: CreditsDataTableProps) {
  // Get the billing cycle start day from Stripe subscription, fallback to Counso subscription
  const getBillingCycleStartDay = (): number | null => {
    if (stripeSubscription?.current_period_start) {
      return new Date(stripeSubscription.current_period_start * 1000).getDate();
    }
    if (subscription.startDate) {
      return new Date(subscription.startDate).getDate();
    }
    return null;
  };
  const billingCycleStartDay = getBillingCycleStartDay();

  return (
    <>
      <PokeDataTableConditionalFetch<PokeCreditsData, PokeCreditsData>
        header="API Usage"
        owner={owner}
        loadOnInit={loadOnInit}
        useSWRHook={usePokeCredits}
      >
        {(data) => (
          <div className="space-y-4">
            {data.excessCreditsLast30DaysMicroUsd > ONE_DOLLAR_MICRO_USD && (
              <div className="rounded-md border border-warning-200 bg-warning-50 p-3">
                <Tooltip
                  label="Excess credits are created when programmatic usage exceeds available credits. This tracks over-consumption that needs to be billed."
                  trigger={
                    <p className="cursor-help text-sm font-medium text-warning-800">
                      Excess credits (last 30 days):{" "}
                      {formatMicroUsdToUsd(
                        data.excessCreditsLast30DaysMicroUsd
                      )}
                    </p>
                  }
                />
              </div>
            )}
            <PokeDataTable
              columns={makeColumnsForCredits()}
              data={sortRowsByStartDateDescending(data.rows)}
              defaultFilterColumn="type"
            />
          </div>
        )}
      </PokeDataTableConditionalFetch>

      {billingCycleStartDay && (
        <div className="flex flex-col gap-4">
          <Suspense fallback={<PokeChartFallback />}>
            <PokeProgrammaticCostChart
              owner={owner}
              billingCycleStartDay={billingCycleStartDay}
            />
          </Suspense>
        </div>
      )}
    </>
  );
}
